import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useSafeLayoutEffect } from '@tmap-web-lib/utils/react'
import { forwardRef, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'

import { SafeAreaContext } from './safe-area-context'

// 텍스트 컬러 변경시 enqueueTask의 필요 이유.
// TmapApp.getUserSetting(night모드에 여부 확인) 값을 비동기로 받아 텍스트 컬러를 결정하기 때문에
// 화면이 쌓이고 빠지는 순서대로 텍스트 컬러가 변경/복원되도록 queue를 통해 순서대로 실행되게 해야함.
// 실행 순서를 제어하지 않으면 가장 늦게 호출한 TmapApp.getUserSetting이 가장 먼저 fulfilled 상태가 될 수 있기 떄문에
// 변경/복원/복원/변경/변경/복원 같이 예상할 수 없는 순서 적용돼 이전 텍스트 컬러로 복원되지 않게됨.
export const SafeAreaProvider = forwardRef<SafeAreaRef, Props>(function SafeAreaProvider(
  { disableInit, initialSafeAreaTextColor = 'black', children },
  ref
) {
  const [insetTop, setInsetTop] = useState(0)
  const textColorStackRef = useRef<Array<Exclude<SafeAreaTextColor, 'auto'>>>([])
  const queue = useRef(Promise.resolve())
  const disableInitRef = useRef(disableInit)
  const [inited, setInited] = useState(disableInit)

  const enqueueTask = useCallback((task: () => Promise<void> | void) => {
    queue.current = queue.current.then(task)
  }, [])

  const getAutoTextColor = useCallback(async () => {
    const result = await TmapApp.getUserSetting({ key: 'local.useMapNightMode' })
    return result === 'true' ? 'white' : 'black'
  }, [])

  const expand = useCallback(async (use: boolean) => {
    const displayInfo = await TmapApp.getDisplayInfo({ $toDip: true })
    if (!displayInfo) return
    const insetTop = use ? displayInfo.statusBarHeight : 0
    setInsetTop(insetTop)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TmapApp.useStatusBarArea({ use })
  }, [])

  const changeTextColor = useCallback(
    (color: SafeAreaTextColor) => {
      enqueueTask(async () => {
        const textColor = color === 'auto' ? await getAutoTextColor() : color
        textColorStackRef.current.push(textColor)
        TmapApp.setStatusBarTextColor({ color: textColor })
      })
      const revertTextColor = () => {
        enqueueTask(() => {
          textColorStackRef.current.pop()
          const prevColor = textColorStackRef.current[textColorStackRef.current.length - 1]
          if (prevColor) {
            TmapApp.setStatusBarTextColor({ color: prevColor })
          }
        })
      }
      return revertTextColor
    },
    [enqueueTask, getAutoTextColor]
  )

  const value = useMemo<SafeAreaRef>(
    () => ({
      insetTop,
      expand,
      changeTextColor,
    }),
    [insetTop, expand, changeTextColor]
  )

  useSafeLayoutEffect(() => {
    if (typeof ref === 'function') {
      ref?.(value)
    } else if (ref) {
      ref.current = value
    }
  }, [ref, value])

  useSafeLayoutEffect(() => {
    if (disableInitRef.current) return

    expand(true).then(() => setInited(true))

    const handler = () => {
      TmapApp.getDisplayInfo({ $toDip: true }).then((displayInfo) => {
        if (!displayInfo) return
        setInsetTop(displayInfo.statusBarHeight)
      })
    }
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [expand])

  useSafeLayoutEffect(() => {
    if (disableInitRef.current) return
    changeTextColor(initialSafeAreaTextColor)
  }, [changeTextColor, initialSafeAreaTextColor])

  return <SafeAreaContext.Provider value={value}>{inited ? children : null}</SafeAreaContext.Provider>
})

interface Props extends PropsWithChildren {
  initialSafeAreaTextColor?: SafeAreaTextColor
  disableInit?: boolean
}

export interface SafeAreaRef {
  insetTop: number
  expand: (use: boolean) => void
  changeTextColor: (color: SafeAreaTextColor) => () => void
}

export type SafeAreaTextColor = 'black' | 'white' | 'auto'
